"use client";

import { motion, useAnimation, useReducedMotion, Variants } from "framer-motion";
import { forwardRef, useCallback, useImperativeHandle, useRef } from "react";

export interface SparklesIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

interface SparklesIconProps {
  size?: number;
  duration?: number;
  isAnimated?: boolean;
  isHovered?: boolean;
  className?: string;
}

const SparklesIcon = forwardRef<SparklesIconHandle, SparklesIconProps>(
  (
    {
      className,
      size = 24,
      duration = 1,
      isAnimated = true,
      isHovered = false,
    },
    ref,
  ) => {
    const controls = useAnimation();
    const reduced = useReducedMotion();
    const isControlled = useRef(false);
    const prevHovered = useRef(isHovered);

    useImperativeHandle(ref, () => {
      isControlled.current = true;
      return {
        startAnimation: () =>
          reduced ? controls.start("normal") : controls.start("animate"),
        stopAnimation: () => controls.start("normal"),
      };
    });

    // React to isHovered prop changes
    if (isHovered !== prevHovered.current) {
      prevHovered.current = isHovered;
      if (isHovered && isAnimated && !reduced) {
        controls.start("animate");
      } else if (!isHovered) {
        controls.start("normal");
      }
    }

    const handleEnter = useCallback(() => {
      if (!isAnimated || reduced) return;
      if (!isControlled.current) controls.start("animate");
    }, [controls, reduced, isAnimated]);

    const handleLeave = useCallback(() => {
      if (!isControlled.current) controls.start("normal");
    }, [controls]);

    const iconVariants: Variants = {
      normal: { scale: 1, rotate: 0 },
      animate: {
        scale: [1, 1.06, 0.98, 1],
        rotate: [0, -2, 1, 0],
        transition: {
          duration: 0.85 * duration,
          ease: [0.22, 1, 0.36, 1],
        },
      },
    };

    const starVariants: Variants = {
      normal: { opacity: 1, scale: 1 },
      animate: {
        opacity: [0.6, 1, 1],
        scale: [0.7, 1.15, 1],
        transition: {
          duration: 0.7 * duration,
          ease: "easeOut",
          delay: 0.05,
        },
      },
    };

    const crossVariants: Variants = {
      normal: { opacity: 0.9, scale: 1, rotate: 0 },
      animate: {
        opacity: [0, 1],
        scale: [0.4, 1],
        rotate: [-45, 0],
        transition: {
          duration: 0.55 * duration,
          ease: "easeOut",
          delay: 0.16,
        },
      },
    };

    const dotVariants: Variants = {
      normal: { opacity: 1, scale: 1, y: 0 },
      animate: {
        opacity: [0, 1],
        scale: [0.4, 1],
        y: [4, 0],
        transition: {
          duration: 0.5 * duration,
          ease: "easeOut",
          delay: 0.28,
        },
      },
    };

    return (
      <motion.svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        animate={controls}
        initial="normal"
        variants={iconVariants}
        className={className}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        style={{ flexShrink: 0 }}
      >
        <motion.path
          d="M11.017 2.814a1 1 0 0 1 1.966 0l1.051 5.558a2 2 0 0 0 1.594 1.594l5.558 1.051a1 1 0 0 1 0 1.966l-5.558 1.051a2 2 0 0 0-1.594 1.594l-1.051 5.558a1 1 0 0 1-1.966 0l-1.051-5.558a2 2 0 0 0-1.594-1.594l-5.558-1.051a1 1 0 0 1 0-1.966l5.558-1.051a2 2 0 0 0 1.594-1.594z"
          variants={starVariants}
          initial="normal"
          animate={controls}
        />
        <motion.path
          d="M20 2v4"
          variants={crossVariants}
          initial="normal"
          animate={controls}
        />
        <motion.path
          d="M22 4h-4"
          variants={crossVariants}
          initial="normal"
          animate={controls}
        />
        <motion.circle
          cx="4"
          cy="20"
          r="2"
          variants={dotVariants}
          initial="normal"
          animate={controls}
        />
      </motion.svg>
    );
  },
);

SparklesIcon.displayName = "SparklesIcon";
export { SparklesIcon };
